// src/admin/Dashboard.jsx
import React, { useEffect, useState } from "react";

const API_BASE = "https://test-env2.triples-sa.com/api";

export default function Dashboard() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState({});
  const [textEdits, setTextEdits] = useState({});
  const [arrayEdits, setArrayEdits] = useState({});
  const [msg, setMsg] = useState(null);

  // تحميل البيانات من الـ API
  useEffect(() => {
    fetch(`${API_BASE}/get_content.php`)
      .then((r) => r.json())
      .then((data) => {
        setContent(data || {});
        const texts = {};
        const arrays = {};
        for (const section of Object.keys(data || {})) {
          for (const key of Object.keys(data[section] || {})) {
            const value = data[section][key];
            const name = `${section}__${key}`;
            if (Array.isArray(value)) arrays[name] = value.slice();
            else texts[name] = value ?? "";
          }
        }
        setTextEdits(texts);
        setArrayEdits(arrays);
      })
      .catch((err) => {
        console.error("get_content error", err);
        setContent({});
      })
      .finally(() => setLoading(false));
  }, []);

  // تحديد نوع الحقل
  const detectType = (val) => {
    if (Array.isArray(val)) return "json";
    if (!val) return "text";
    if (typeof val === "string") {
      const v = val.trim();
      if (v.startsWith("[") || v.startsWith("{")) return "json";
      if (v.startsWith("/") || v.startsWith("http")) return "image";
    }
    return "text";
  };

  // تحديث النصوص
  const handleTextChange = (name, value) => {
    setTextEdits((s) => ({ ...s, [name]: value }));
  };

  // تحديث الصور
  const handleFileChange = (name, file) => {
    setFiles((s) => ({ ...s, [name]: file }));
  };

  // تحديث المصفوفات
  const handleArrayChange = (name, arr) => {
    setArrayEdits((s) => ({ ...s, [name]: arr }));
  };

  // حذف عنصر من مصفوفة
  const removeArrayItem = (name, idx) => {
    setArrayEdits((s) => {
      const copy = (s[name] || []).slice();
      copy.splice(idx, 1);
      return { ...s, [name]: copy };
    });
  };

  // إضافة عنصر للمصفوفة
  const addArrayItem = (name, value = "") => {
    setArrayEdits((s) => {
      const copy = (s[name] || []).slice();
      copy.push(value);
      return { ...s, [name]: copy };
    });
  };

  // الحفظ
  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);

    try {
      const fd = new FormData();

      // النصوص
      for (const k of Object.keys(textEdits)) {
        fd.append(k, textEdits[k] ?? "");
      }

      // المصفوفات كـ JSON
      for (const k of Object.keys(arrayEdits)) {
        const jsonStr = JSON.stringify(arrayEdits[k]);
        fd.append(k + "_json", jsonStr);
      }

      // الصور
      for (const k of Object.keys(files)) {
        fd.append(k, files[k]);
      }

      const resp = await fetch(`${API_BASE}/update_content.php`, {
        method: "POST",
        body: fd,
      });

      const text = await resp.text();
      if (resp.ok) {
        setMsg({ type: "success", text: "✅ تم حفظ التعديلات بنجاح" });
        // إعادة تحميل المحتوى من السيرفر بعد التحديث
        const re = await fetch(`${API_BASE}/get_content.php`);
        const data = await re.json();
        setContent(data || {});
        setFiles({});
      } else {
        setMsg({ type: "error", text: "❌ خطأ أثناء الحفظ" });
      }
    } catch (err) {
      console.error(err);
      setMsg({ type: "error", text: "⚠️ حدث خطأ أثناء الاتصال بالسيرفر" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <h3>جارٍ تحميل المحتوى...</h3>
      </div>
    );
  }

  // 🎨 تصميم الواجهة
  return (
    <div style={{ minHeight: "100vh", background: "#f6f8fa", padding: "20px" }}>
      <div style={{ maxWidth: 1100, margin: "auto" }}>
        <h1>📋 لوحة تحكم المحتوى</h1>
        <p>تحكم في النصوص والصور مباشرة من هنا</p>

        {msg && (
          <div
            style={{
              padding: 12,
              borderRadius: 8,
              background: msg.type === "success" ? "#e6f4ea" : "#fff1f0",
              color: msg.type === "success" ? "#0b7a2b" : "#7a1a1a",
              marginBottom: 16,
            }}
          >
            {msg.text}
          </div>
        )}

        <div style={{ display: "grid", gap: 20, gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))" }}>
          {Object.keys(content || {}).map((section) => (
            <div key={section} style={{ background: "#fff", padding: 20, borderRadius: 10, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
              <h2 style={{ marginTop: 0, color: "#1a73e8" }}>{section}</h2>

              {Object.keys(content[section] || {}).map((key) => {
                const value = content[section][key];
                const name = `${section}__${key}`;
                const type = detectType(value);

                if (type === "image") {
                  const previewUrl = files[name]
                    ? URL.createObjectURL(files[name])
                    : value || "";

                  return (
                    <div key={name} style={{ marginBottom: 16 }}>
                      <strong>{key}:</strong>
                      <br />
                      {previewUrl && (
                        <img
                          src={`https://test-env2.triples-sa.com/${previewUrl}`}
                          alt={key}
                          style={{
                            width: "100%",
                            maxWidth: "260px",
                            height: "auto",
                            borderRadius: 8,
                            marginTop: 6,
                          }}
                        />
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileChange(name, e.target.files[0])}
                        style={{ marginTop: 8 }}
                      multiple
                      />
                    </div>
                  );
                }

                if (type === "json") {
                  const arr = arrayEdits[name] ?? [];
                  return (
                    <div key={name} style={{ marginBottom: 16 }}>
                      <strong>{key}:</strong>
                      {arr.map((item, idx) => (
                        <div key={idx} style={{ display: "flex", gap: 6, marginTop: 6 }}>
                          <input
                            value={item}
                            onChange={(e) => {
                              const copy = [...arr];
                              copy[idx] = e.target.value;
                              handleArrayChange(name, copy);
                            }}
                            style={{ flex: 1, padding: 8 }}
                          />
                          <button onClick={() => removeArrayItem(name, idx)}>❌</button>
                        </div>
                      ))}
                      <button onClick={() => addArrayItem(name, "")} style={{ marginTop: 6 }}>
                        ➕ إضافة عنصر
                      </button>
                    </div>
                  );
                }

                // نصوص
                return (
                  <div key={name} style={{ marginBottom: 16 }}>
                    <strong>{key}:</strong>
                    <textarea
                      rows={3}
                      style={{ width: "100%", padding: 8, marginTop: 6 }}
                      value={textEdits[name] ?? ""}
                      onChange={(e) => handleTextChange(name, e.target.value)}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div style={{ marginTop: 20, textAlign: "right" }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              background: "#1a73e8",
              color: "#fff",
              padding: "10px 18px",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {saving ? "جارٍ الحفظ..." : "💾 حفظ التعديلات"}
          </button>
        </div>
      </div>
    </div>
  );
}
